import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useUI } from '../../contexts/UIContext'
import NotificationBell from '../notifications/NotificationBell'
import { 
  Bars3Icon, 
  MoonIcon, 
  SunIcon,
  UserCircleIcon,
  PlusIcon,
  BookOpenIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  BanknotesIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'

const Header: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, isSignedIn, user, clerkUser, signOut, canCreateContent, canModerate } = useAuth()
  const { openAuthModal, isDark, setTheme } = useUI()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // Use Clerk user for display name/initials while Convex user may still be loading
  const displayName = user?.displayName ?? clerkUser?.fullName ?? clerkUser?.username ?? 'Account'
  const avatarUrl = user?.avatar ?? clerkUser?.imageUrl

  const handleAuthClick = () => {
    openAuthModal()
  }

  const handleSignOut = () => {
    signOut()
    setShowUserMenu(false)
    navigate('/')
  }

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <header className="bg-white dark:bg-gray-900 shadow-lg border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={isSignedIn ? "/storefront" : "/"} className="flex items-center group">
              <div className="flex-shrink-0">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-md">
                    <BookOpenIcon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                    StoryBranch
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:block">
            <div className="flex items-center space-x-6">
              <Link 
                to="/storefront" 
                className="nav-link-fix px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Explore Stories
              </Link>

              {canModerate() && (
                <Link 
                  to="/import/gutenberg" 
                  className="nav-link-fix px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                  Import Book
                </Link>
              )}
              
              {canCreateContent() && (
                <Link 
                  to="/editor" 
                  className="nav-link-fix px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Create
                </Link>
              )}
              
              {isAuthenticated && (
                <Link 
                  to="/library" 
                  className="nav-link-fix px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  My Library
                </Link>
              )}
            </div>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            {/* Theme toggle */}
            <button
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="p-2 text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-all duration-200"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>

            {/* StoryCoins balance (for authenticated users) */}
            {isAuthenticated && user && (
              <Link
                to="/profile?tab=wallet"
                className="hidden sm:flex items-center bg-gradient-to-r from-accent-50 to-accent-100 dark:from-accent-900 dark:to-accent-800 text-accent-700 dark:text-accent-300 px-3 py-1.5 rounded-full text-sm font-medium hover:shadow-md transition-all duration-200"
                title="View wallet & buy StoryCoins"
              >
                <svg className="h-4 w-4 text-accent-600 dark:text-accent-400 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.51-1.31c-.562-.649-1.413-1.076-2.353-1.253V5z" clipRule="evenodd" />
                </svg>
                {user.storyCoins.toLocaleString()}
                <PlusIcon className="h-3.5 w-3.5 ml-1.5 opacity-60" />
              </Link>
            )}

            {/* Notification bell */}
            <NotificationBell />

            {/* User menu or auth button */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-all duration-200"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {getUserInitials(displayName)}
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {displayName}
                  </span>
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-800 rounded-xl shadow-strong border border-neutral-200 dark:border-neutral-700 py-1 z-50">
                      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {displayName}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          {user?.email ?? clerkUser?.primaryEmailAddress?.emailAddress ?? ''}
                        </p>
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                          Role: {user?.role ?? 'reader'}
                        </p>
                      </div>
                      
                      <Link
                        to={`/user/${user?.username ?? ''}`}
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <UserCircleIcon className="w-4 h-4 mr-3" />
                        Profile
                      </Link>
                      
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Cog6ToothIcon className="w-4 h-4 mr-3" />
                        Profile Settings
                      </Link>

                      <Link
                        to="/profile?tab=wallet"
                        className="flex items-center px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <BanknotesIcon className="w-4 h-4 mr-3" />
                        Wallet
                        <span className="ml-auto text-xs font-medium text-accent-600 dark:text-accent-400">{user?.storyCoins?.toLocaleString() ?? 0} SC</span>
                      </Link>
                      


                      {canModerate() && (
                        <Link
                          to="/admin"
                          className="flex items-center px-4 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <ChartBarIcon className="w-4 h-4 mr-3" />
                          Admin Dashboard
                        </Link>
                      )}
                      
                      <div className="border-t border-neutral-200 dark:border-neutral-700 my-1"></div>
                      
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={handleAuthClick}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm"
              >
                Sign In
              </button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-all duration-200"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {showMobileMenu && (
        <nav className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 space-y-1">
          <Link
            to="/storefront"
            className="block px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => setShowMobileMenu(false)}
          >
            Explore Stories
          </Link>
          {canModerate() && (
            <Link
              to="/import/gutenberg"
              className="block px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setShowMobileMenu(false)}
            >
              <ArrowDownTrayIcon className="w-4 h-4 inline mr-1" />
              Import Book
            </Link>
          )}
          {canCreateContent() && (
            <Link
              to="/editor"
              className="block px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setShowMobileMenu(false)}
            >
              <PlusIcon className="w-4 h-4 inline mr-1" />
              Create
            </Link>
          )}
          {isAuthenticated && (
            <Link
              to="/library"
              className="block px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setShowMobileMenu(false)}
            >
              My Library
            </Link>
          )}
        </nav>
      )}
    </header>
  )
}

export default Header
